import React, { useEffect, useState } from "react";
import useAuthStore from "@/stores/user/authStore";
import { supabase } from "@/config/supabaseClient";
import { useNavigate } from "react-router-dom";
import { formatImageUrl } from "@/lib/general";
import { motion } from "framer-motion";
import { ReactTyped } from "react-typed";
import waytora_logo from "@/assets/logos/internal/waytora_blue_text_logo.svg";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import logger from "@/utils/logger";
const loadingContent = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2, ease: "easeOut", delay: 0.3 }}
      className="text-center space-y-8"
    >
      <div className="space-y-6">
        <ReactTyped
          strings={[
            "Getting everything ready...",
            "Just a few more moments...",
            "Configuring your travel preferences...",
          ]}
          typeSpeed={40}
          backSpeed={30}
          backDelay={1000}
          loop
          className="text-lg text-neutral-600 font-light"
        />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex items-center justify-center space-x-2"
        >
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
          <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" />
        </motion.div>
      </div>
    </motion.div>
  );
};

const Callback: React.FC = () => {
  logger.log("[Callback] Rendering");
  const navigate = useNavigate();
  const { user, profile_image, handleAuthCallback } = useAuthStore();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showLoadingContent, setShowLoadingContent] = useState(true);
  const [showMainContent, setShowMainContent] = useState(false);

  const isDataReady = user && user.profile_image && imageLoaded;

  useEffect(() => {
    logger.log("[Auth] Getting user session...");
    const getUser = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("[Auth] Error getting session:", error);
      }
      logger.log("[Auth] Session data:", data, "Error:", error);
      if (data) {
        await handleAuthCallback(data.session, false);
      }
    };

    getUser();
  }, [handleAuthCallback]);

  useEffect(() => {
    logger.log("[Image] Profile image changed:", profile_image);
    if (profile_image) {
      const img = new Image();
      img.src = formatImageUrl(profile_image, "medium");
      img.onload = () => {
        logger.log("[Image] Profile image loaded successfully");
        setImageLoaded(true);
      };
    }
  }, [profile_image]);

  useEffect(() => {
    logger.log("[State] Current state:", {
      user: !!user,
      profile_image: !!profile_image,
      imageLoaded,
      isDataReady,
      showLoadingContent,
      showMainContent,
    });

    if (isDataReady) {
      logger.log("[Content] Data is ready, showing loading content");
      setShowLoadingContent(true);
      const timer = setTimeout(() => {
        logger.log("[Content] Timer completed, showing main content");
        setShowMainContent(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    isDataReady,
    user,
    profile_image,
    imageLoaded,
    showLoadingContent,
    showMainContent,
  ]);

  const handleGetStarted = () => {
    navigate("/lodge");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 relative">
      <motion.img
        src={waytora_logo}
        alt="Waytora Logo"
        className="absolute top-4 left-1/2 -translate-x-1/2 w-48"
        initial={{ opacity: 0 }}
        animate={{ opacity: showMainContent ? 1 : 0.3 }}
        transition={{ duration: 1, ease: "easeInOut" }}
      />
      {showMainContent ? (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1,
            ease: [0.43, 0.13, 0.23, 0.96],
            delay: 0.3,
          }}
        >
          <div className="text-center space-y-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex items-center justify-center"
            >
              <AvatarImage
                src={profile_image || ""}
                className="w-28 h-28 rounded-full transition-all duration-300 hover:scale-105"
              />
            </motion.div>
            <motion.div
              className="space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              <h1 className="text-2xl font-semibold text-neutral-800">
                Welcome, {user?.first_name || "traveler"}!
              </h1>
              <p className="text-neutral-600 font-light text-sm">
                Your account is ready to go
              </p>
            </motion.div>
            <motion.button
              onClick={handleGetStarted}
              className="mx-auto px-6 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 
                     transition-colors duration-200 flex items-center justify-center space-x-2 group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
            >
              <p className="text-sm font-medium">Get Started</p>
              <i className="fa-solid fa-plane text-sm -rotate-90 group-hover:rotate-0 transition-all duration-300"></i>
            </motion.button>
          </div>
        </motion.div>
      ) : showLoadingContent ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
        >
          {loadingContent()}
        </motion.div>
      ) : null}
    </div>
  );
};

export default Callback;
